import React, { useState } from 'react';
import { FileText, Copy, Download, Code, ChevronDown, ChevronRight, GitCompare, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { YAMLFix } from '@/api/websocket';
import { createDiff } from './chat-utils';

export function YAMLFixesDisplay({ yamlFixes }: { yamlFixes: YAMLFix[] }) {
  const [expandedFixes, setExpandedFixes] = useState<Set<number>>(new Set());
  const [showDiffView, setShowDiffView] = useState<Set<number>>(new Set());
  const [copiedFix, setCopiedFix] = useState<number | null>(null);

  const toggleFixExpansion = (fixIndex: number) => {
    setExpandedFixes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixIndex)) newSet.delete(fixIndex);
      else newSet.add(fixIndex);
      return newSet;
    });
  };

  const toggleDiffView = (fixIndex: number) => {
    setShowDiffView(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fixIndex)) newSet.delete(fixIndex);
      else newSet.add(fixIndex);
      return newSet;
    });
  };

  const copyToClipboard = async (content: string, fixIndex: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedFix(fixIndex);
      setTimeout(() => setCopiedFix(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadFile = (content: string, filePath: string) => {
    const blob = new Blob([content], { type: 'text/yaml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = filePath.split('/').pop() || 'fix.yaml';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFileTypeBadge = (fileType: string) => {
    switch (fileType) {
      case 'create': return <Badge className="bg-muted text-foreground border-border">Create</Badge>;
      case 'update': return <Badge className="bg-muted text-foreground border-border">Update</Badge>;
      case 'delete': return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">Delete</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-px h-6 bg-border"></div>
        <h4 className="text-sm font-semibold text-foreground">Remediation Steps</h4>
        <Badge variant="outline" className="text-xs">
          {yamlFixes.length} {yamlFixes.length === 1 ? 'fix' : 'fixes'}
        </Badge>
      </div>

      {yamlFixes.map((fix, index) => {
        const isExpanded = expandedFixes.has(index);
        const isCopied = copiedFix === index;

        return (
          <Card key={index} className="border-l-2 border-l-border overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{fix.file_path}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {getFileTypeBadge(fix.file_type)}
                      {fix.resource_name && (
                        <span className="text-xs text-muted-foreground truncate">
                          {fix.resource_kind}/{fix.resource_name}
                          {fix.resource_namespace && ` in ${fix.resource_namespace}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(fix.yaml_content, index)} className="text-xs">
                    <Copy className="h-3 w-3 mr-1" />
                    {isCopied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadFile(fix.yaml_content, fix.file_path)} className="text-xs">
                    <Download className="h-3 w-3 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {fix.explanation && (
                <div>
                  <h5 className="text-xs font-medium text-foreground mb-2">What this fixes:</h5>
                  <p className="text-xs text-muted-foreground">{fix.explanation}</p>
                </div>
              )}

              <Collapsible open={isExpanded} onOpenChange={() => toggleFixExpansion(index)}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                    <span className="flex items-center gap-2">
                      <Code className="h-3 w-3" />
                      View YAML Configuration
                    </span>
                    {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3">
                  <div className="space-y-3">
                    {fix.existing_yaml && fix.file_type === 'update' && (
                      <div className="flex justify-center">
                        <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                          <Button variant={!showDiffView.has(index) ? "default" : "ghost"} size="sm"
                            onClick={() => setShowDiffView(prev => { const s = new Set(prev); s.delete(index); return s; })} className="text-xs h-6">
                            <Eye className="h-3 w-3 mr-1" /> New YAML
                          </Button>
                          <Button variant={showDiffView.has(index) ? "default" : "ghost"} size="sm"
                            onClick={() => toggleDiffView(index)} className="text-xs h-6">
                            <GitCompare className="h-3 w-3 mr-1" /> Show Diff
                          </Button>
                        </div>
                      </div>
                    )}

                    {fix.existing_yaml && fix.file_type === 'update' && showDiffView.has(index) ? (
                      <div className="bg-muted p-3 rounded-md border font-mono text-xs overflow-auto max-h-64">
                        {createDiff(fix.existing_yaml, fix.yaml_content).map((diffLine, lineIndex) => (
                          <div key={lineIndex} className={cn(
                            "whitespace-pre-wrap flex",
                            diffLine.type === 'removed' && "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200",
                            diffLine.type === 'added' && "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200",
                            diffLine.type === 'unchanged' && "text-foreground hover:bg-muted/50"
                          )}>
                            <span className={cn(
                              "inline-block w-8 text-right mr-3 text-muted-foreground select-none flex-shrink-0 border-r border-border/50 pr-2",
                              diffLine.type === 'removed' && "text-red-600 dark:text-red-400",
                              diffLine.type === 'added' && "text-green-600 dark:text-green-400"
                            )}>
                              {diffLine.type === 'removed' ? '-' : diffLine.type === 'added' ? '+' : ' '}
                            </span>
                            <span className="flex-1 min-w-0">{diffLine.line}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre className={cn(
                        "p-4 rounded-md border font-mono text-xs overflow-x-auto",
                        fix.file_type === 'delete' ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                          : fix.file_type === 'create' ? "bg-muted/50 border-border"
                          : "bg-muted"
                      )}>
                        <code className="text-foreground">
                          {fix.file_type === 'delete' && fix.existing_yaml ? fix.existing_yaml : fix.yaml_content}
                        </code>
                      </pre>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
